import { useState } from "react";
import {
  Alert,
  Box,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  capitalize,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "react-i18next";
import {
  colorLandingBrand,
  colorLandingBrandSoft,
  colorLandingBrandStrong,
  colorLandingLine,
  colorLandingSurface,
  colorLandingTint,
  colorTextError,
  fontLandingDisplay,
  radiusLandingControl,
  shadowLandingRaised,
  colorLandingBrandRgb,
  colorLandingOnBrandShadowRgb,
} from "src/design/tokens";
import { useCreateDemoRequest } from "src/data/landing/queries/demoRequest";
import { tierTitleKey } from "src/business/subscription/tierLabel";
import { SubscriptionTiersModal } from "src/components/LandingPage/components/SubscriptionTiersModal";
import { LandingButton } from "./components/LandingButton";
import restaurantInterior from "src/assets/landing/restaurant-interior.jpg";

interface FormState {
  managerName: string;
  email: string;
  phone: string;
  restaurantName: string;
  city: string;
  howDidYouHear: string;
  subscriptionTier: string;
  message: string;
}

const INITIAL: FormState = {
  managerName: "",
  email: "",
  phone: "",
  restaurantName: "",
  city: "",
  howDidYouHear: "",
  subscriptionTier: "",
  message: "",
};

const CITY_KEYS = [
  "casablanca",
  "rabat",
  "marrakech",
  "fes",
  "tanger",
  "agadir",
  "other",
];
const HEAR_KEYS = ["socialMedia", "friend", "google", "event", "other"];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: radiusLandingControl,
    backgroundColor: colorLandingSurface,
    "& fieldset": { borderColor: colorLandingLine },
    "&:hover fieldset": { borderColor: colorLandingBrandStrong },
    "&.Mui-focused fieldset": { borderColor: colorLandingBrand },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: colorLandingBrand },
};

/**
 * Closing section: the gradient invitation banner with the form card lapping
 * over its lower edge. The submission contract is unchanged — the plan is
 * still picked from the live subscription catalog via the tiers modal.
 */
export const DemoFormSection = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [tierModalOpen, setTierModalOpen] = useState(false);
  const [tierError, setTierError] = useState(false);
  const { mutate, isPending, isSuccess, isError, error } = useCreateDemoRequest();

  const errorKey = (() => {
    if (!isError || !error) return "landingPage.demo.error";
    const message = (error as Error).message;
    if (message.includes("Email")) return "landingPage.demo.errorEmailTaken";
    if (message.includes("Phone")) return "landingPage.demo.errorPhoneTaken";
    return "landingPage.demo.error";
  })();

  const handleChange =
    (field: keyof FormState) =>
    (event: { target: { value: unknown } }) => {
      setForm((previous) => ({
        ...previous,
        [field]: String(event.target.value),
      }));
    };

  const handleSelectTier = (tierName: string) => {
    setForm((previous) => ({ ...previous, subscriptionTier: tierName }));
    setTierError(false);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.subscriptionTier) {
      setTierError(true);
      return;
    }
    const [firstName, ...rest] = form.managerName.trim().split(" ");
    const lastName = rest.join(" ") || " ";
    mutate({
      email: form.email,
      firstName,
      lastName,
      phone: form.phone,
      restaurantName: form.restaurantName,
      cityName: capitalize(form.city),
      subscriptionTier: form.subscriptionTier,
      message: form.message || null,
    });
  };

  return (
    <Box
      component="section"
      id="demo"
      sx={{
        py: { xs: 8, md: 11.5 },
        backgroundColor: colorLandingTint,
        borderTop: `1px solid ${colorLandingLine}`,
      }}
    >
      <Container maxWidth="lg" sx={{ overflow: "visible" }}>
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: "28px",
            textAlign: "center",
            px: { xs: 2.75, md: 5.25 },
            pt: { xs: 5, md: 6.75 },
            pb: { xs: 8.25, md: 9.25 },
            background: `linear-gradient(135deg, ${colorLandingBrand}, ${colorLandingBrandStrong})`,
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: { xs: "100%", sm: "56%" },
              backgroundImage: `url(${restaurantInterior})`,
              backgroundPosition: "center",
              backgroundSize: "cover",
              zIndex: 1,
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: {
                  xs: `linear-gradient(180deg, rgba(${colorLandingBrandRgb}, .45) 0%, rgba(${colorLandingBrandRgb}, .9) 62%, ${colorLandingBrand} 100%)`,
                  sm: `linear-gradient(90deg, rgba(${colorLandingBrandRgb}, 0) 0%, rgba(${colorLandingBrandRgb}, 0) 28%, rgba(${colorLandingBrandRgb}, .55) 58%, ${colorLandingBrand} 92%)`,
                },
              },
            }}
          />

          <Box sx={{ position: "relative", zIndex: 2 }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: fontLandingDisplay,
                fontWeight: 400,
                fontSize: { xs: 26, md: 42 },
                lineHeight: 1.08,
                color: colorLandingSurface,
                textShadow: `0 1px 14px rgba(${colorLandingOnBrandShadowRgb}, .25)`,
              }}
            >
              {t("landingPage.demo.title")}
            </Typography>
            <Typography
              sx={{
                mt: 1.75,
                mx: "auto",
                maxWidth: "52ch",
                fontSize: 17,
                lineHeight: 1.6,
                color: `${colorLandingSurface}F0`,
                textShadow: `0 1px 12px rgba(${colorLandingOnBrandShadowRgb}, .2)`,
              }}
            >
              {t("landingPage.demo.subtitle")}
            </Typography>
          </Box>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            position: "relative",
            zIndex: 3,
            maxWidth: 760,
            mx: "auto",
            mt: { xs: -4.25, md: -5.75 },
            backgroundColor: colorLandingSurface,
            border: `1px solid ${colorLandingLine}`,
            borderRadius: "22px",
            boxShadow: shadowLandingRaised,
            p: { xs: 2.5, md: 4 },
          }}
        >
          {isSuccess ? (
            <Alert severity="success" sx={{ borderRadius: radiusLandingControl }}>
              {t("landingPage.demo.success")}
            </Alert>
          ) : (
            <Grid container spacing={1.875}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={t("landingPage.demo.fields.managerName")}
                  value={form.managerName}
                  onChange={handleChange("managerName")}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label={t("landingPage.demo.fields.email")}
                  value={form.email}
                  onChange={handleChange("email")}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="tel"
                  label={t("landingPage.demo.fields.phone")}
                  value={form.phone}
                  onChange={handleChange("phone")}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={t("landingPage.demo.fields.restaurantName")}
                  value={form.restaurantName}
                  onChange={handleChange("restaurantName")}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>{t("landingPage.demo.fields.city")}</InputLabel>
                  <Select
                    value={form.city}
                    label={t("landingPage.demo.fields.city")}
                    onChange={handleChange("city")}
                  >
                    {CITY_KEYS.map((key) => (
                      <MenuItem key={key} value={key}>
                        {t(`landingPage.demo.cityOptions.${key}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>
                    {t("landingPage.demo.fields.howDidYouHear")}
                  </InputLabel>
                  <Select
                    value={form.howDidYouHear}
                    label={t("landingPage.demo.fields.howDidYouHear")}
                    onChange={handleChange("howDidYouHear")}
                  >
                    {HEAR_KEYS.map((key) => (
                      <MenuItem key={key} value={key}>
                        {t(`landingPage.demo.hearOptions.${key}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <LandingButton
                  fullWidth
                  variant={form.subscriptionTier ? "brand" : "ghost"}
                  startIcon={
                    form.subscriptionTier ? <CheckCircleOutlineIcon /> : undefined
                  }
                  onClick={() => setTierModalOpen(true)}
                  label={
                    form.subscriptionTier
                      ? t("landingPage.subscriptionTiers.buttonSelected", {
                          tier: t(tierTitleKey(form.subscriptionTier), {
                            defaultValue: form.subscriptionTier,
                          }),
                        })
                      : t("landingPage.subscriptionTiers.buttonLabel")
                  }
                  sx={
                    form.subscriptionTier
                      ? undefined
                      : {
                          color: colorLandingBrandStrong,
                          borderWidth: 1.5,
                          borderColor: tierError ? colorTextError : colorLandingBrand,
                          fontWeight: 700,
                          "&:hover": {
                            borderColor: colorLandingBrandStrong,
                            backgroundColor: colorLandingBrandSoft,
                          },
                        }
                  }
                />
                {tierError && (
                  <Typography
                    variant="caption"
                    sx={{ color: colorTextError, mt: 0.75, display: "block" }}
                  >
                    {t("landingPage.subscriptionTiers.required")}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t("landingPage.demo.fields.message")}
                  value={form.message}
                  onChange={handleChange("message")}
                  sx={inputSx}
                />
              </Grid>

              {isError && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ borderRadius: radiusLandingControl }}>
                    {t(errorKey)}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <LandingButton
                  type="submit"
                  fullWidth
                  disabled={isPending}
                  label={t("landingPage.demo.cta")}
                  sx={{ py: 2, fontSize: 16 }}
                />
              </Grid>
            </Grid>
          )}
        </Box>
      </Container>

      <SubscriptionTiersModal
        open={tierModalOpen}
        selectedTier={form.subscriptionTier || null}
        onClose={() => setTierModalOpen(false)}
        onSelect={handleSelectTier}
      />
    </Box>
  );
};
